import s3Service from '../services/s3Service.js';
import PartnerOnboardingAPI from '../services/partnerOnboardingAPI.js';
import { StepHelpers } from '../constants/stepConstants.js';

/**
 * Business Documentation Handlers
 * Contains all business logic for handling business documentation file operations
 * This keeps PartnerDashboard.jsx lightweight
 */

const BUSINESS_STEP_ID = StepHelpers.getApiStepId('businessDocumentation') ?? 9;
const SHARIYAH_STEP_ID = StepHelpers.getApiStepId('shariyah') ?? 5;

/**
 * Creates business documentation handlers
 * @param {Object} params - Handler parameters
 * @param {Function} params.setBusinessUploadedFiles - Setter for uploaded files state
 * @param {Function} params.setIsBusinessUploading - Setter for upload loading state
 * @param {Function} params.setBusinessUploadProgress - Setter for upload progress text
 * @param {Function} params.setBusinessUploadStatus - Setter for upload status text
 * @param {Function} params.setIsViewingBusinessFile - Setter for file viewing state
 * @param {Function} params.setBusinessCompleted - Setter for completion state
 * @param {Function} params.setShowBusinessDocumentation - Setter for modal visibility
 * @param {Array} params.businessUploadedFiles - Current uploaded files
 * @param {Function} params.onboardingData - Current onboarding data
 * @param {Function} params.setOnboardingData - Setter for onboarding data
 * @param {Function} params.updateStateFromAPI - Function to update state from API response
 * @param {Function} params.loadOnboardingData - Function to reload onboarding data
 * @returns {Object} Handler functions
 */
export const createBusinessDocumentationHandlers = ({
  setBusinessUploadedFiles,
  setIsBusinessUploading,
  setBusinessUploadProgress,
  setBusinessUploadStatus,
  setIsViewingBusinessFile,
  setBusinessCompleted,
  setShowBusinessDocumentation,
  businessUploadedFiles,
  onboardingData,
  setOnboardingData,
  updateStateFromAPI,
  loadOnboardingData
}) => {
  const api = new PartnerOnboardingAPI();

  /**
   * Handles business file upload to S3 and database
   */
  const handleBusinessFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    console.log('Business Documentation - Starting file upload for', files.length, 'files');
    setIsBusinessUploading(true);
    setBusinessUploadProgress('Uploading files...');
    setBusinessUploadStatus('Securely storing business documents');

    try {
      const uploadedFileData = [];
      const userId = localStorage.getItem('userId') || 'demo-user-id';

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          setBusinessUploadProgress(`Uploading file ${i + 1} of ${files.length}: ${file.name}`);
          setBusinessUploadStatus(`Securely storing ${file.name}`);
          
          const uploadedFile = await s3Service.uploadFile(file, userId, 'businessDocumentation');
          uploadedFileData.push(uploadedFile);
          setBusinessUploadStatus(`Successfully uploaded ${file.name}`);
        } catch (fileError) {
          console.error(`Error uploading business file ${file.name}:`, fileError);
          alert(`Failed to upload ${file.name}: ${fileError.message}`);
        }
      }

      if (uploadedFileData.length > 0) {
        const newFiles = [...businessUploadedFiles, ...uploadedFileData];
        setBusinessUploadedFiles(newFiles);
        
        try {
          const stepData = {
            businessDocumentation: {
              completed: false,
              files: newFiles.map(file => ({
                name: file.name,
                s3Key: file.s3Key,
                size: file.size,
                uploadDate: file.uploadDate
              }))
            }
          };
          
          const updateResponse = await api.updateStep(userId, BUSINESS_STEP_ID, stepData, BUSINESS_STEP_ID);
          
          if (updateResponse.status === 'success') {
            console.log('Business documentation files saved to database');
            
            // Save to partner_files section
            for (const file of uploadedFileData) {
              try {
                await api.uploadFile(userId, BUSINESS_STEP_ID, file.name, file.s3Key);
              } catch (uploadError) {
                console.warn(`Failed to save business file ${file.name} to partner_files:`, uploadError);
              }
            }
            
            // Refresh onboarding data
            if (loadOnboardingData) {
              await loadOnboardingData();
            }
          }
        } catch (updateError) {
          console.error('Error persisting business documentation files:', updateError);
          setBusinessUploadStatus(`Files uploaded but failed to save to database`);
        }
      }
    } catch (error) {
      console.error('Business Documentation - Error uploading files:', error);
      alert('Error uploading files. Please try again.');
    } finally {
      setIsBusinessUploading(false);
      setBusinessUploadProgress('');
      setBusinessUploadStatus('');
    }
  };

  /**
   * Handles viewing a business file from S3
   */
  const handleViewBusinessFile = async (file) => {
    try {
      setIsViewingBusinessFile(true);
      
      if (!file?.s3Key) {
        alert('File viewing is not available. The file may not have been properly uploaded.');
        return;
      }
      
      const signedUrl = await s3Service.getSignedUrl(file.s3Key);
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing business file:', error);
      alert('Error viewing file. Please try again.');
    } finally {
      setIsViewingBusinessFile(false);
    }
  };

  /**
   * Handles deleting a business file from S3 and database
   */
  const handleDeleteBusinessFile = async (fileId) => {
    const fileToDelete = businessUploadedFiles.find(
      file => file.id === fileId || file.s3Key === fileId
    );
    
    if (!fileToDelete) {
      console.error('Business file not found for deletion:', fileId);
      return;
    }

    try {
      // Delete from S3
      if (fileToDelete.s3Key) {
        await s3Service.deleteFile(fileToDelete.s3Key);
      }
      
      // Update local state
      const updatedFiles = businessUploadedFiles.filter(
        file => file.id !== fileId && file.s3Key !== fileId
      );
      setBusinessUploadedFiles(updatedFiles);
      
      // Update database
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        businessDocumentation: {
          completed: false,
          files: updatedFiles.map(file => ({
            name: file.name,
            s3Key: file.s3Key,
            size: file.size,
            uploadDate: file.uploadDate
          }))
        }
      };
      
      await api.updateStep(userId, BUSINESS_STEP_ID, stepData, BUSINESS_STEP_ID);
    } catch (error) {
      console.error('Error deleting business file:', error);
      alert('Error deleting file. Please try again.');
    }
  };

  /**
   * Handles completing the business documentation step
   */
  const handleCompleteBusinessDocumentation = async () => {
    try {
      const userId = localStorage.getItem('userId') || 'demo-user-id';
      const stepData = {
        businessDocumentation: {
          completed: true,
          completedAt: new Date().toISOString(),
          files: businessUploadedFiles.map(file => ({
            name: file.name,
            s3Key: file.s3Key,
            size: file.size,
            uploadDate: file.uploadDate
          }))
        }
      };
      
      const response = await api.updateStep(userId, BUSINESS_STEP_ID, stepData, SHARIYAH_STEP_ID);
      
      if (response.status === 'success') {
        setBusinessCompleted(true);
        localStorage.setItem('businessCompleted', 'true');
        setShowBusinessDocumentation(false);
        
        // Update local state from API response, preserving user data
        if (response.data.onboarding) {
          const updatedOnboardingData = {
            ...response.data.onboarding,
            user: onboardingData?.user || response.data.onboarding.user
          };
          
          if (setOnboardingData) {
            setOnboardingData(updatedOnboardingData);
          }
          
          if (updateStateFromAPI) {
            updateStateFromAPI(updatedOnboardingData);
          }
        }
        
        console.log('Business documentation step completed successfully');
      } else {
        throw new Error(response.message || 'Failed to complete business documentation');
      }
    } catch (error) {
      console.error('Error completing business documentation:', error);
      alert('Failed to complete submission. Please try again.');
    }
  };

  return {
    handleBusinessFileUpload,
    handleViewBusinessFile,
    handleDeleteBusinessFile,
    handleCompleteBusinessDocumentation
  };
};

